import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, History, Lock, ChevronRight, Calendar } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function CustomerPurchases() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    customerApi.get("/customer/purchases")
      .then((r) => { if (active) setRows(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="page-customer-purchases" className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-8">
      <SEO title="CustomerPurchases · WelkomBiz Photography" noindex />
      <header className="space-y-3">
        <Link to="/customer" data-testid="purchases-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— Purchase History</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
          <span className="text-chrome">Every purchase, </span>
          <span className="italic text-magcyan">recorded</span>
          <span className="text-chrome">.</span>
        </h1>
      </header>

      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       rows.length === 0 ? (
        <div data-testid="purchases-empty" className="glass chrome-border rounded-md p-12 text-center">
          <History className="w-7 h-7 text-[#ff2e93] mx-auto mb-4" />
          <h2 className="font-display text-2xl text-chrome">No purchases yet</h2>
          <p className="text-sm text-white/55 mt-2">Once you confirm a credit purchase, it will appear here.</p>
          <Link to="/events" data-testid="purchases-empty-browse" className="mt-5 inline-flex btn-primary !py-2.5 !px-5 !text-[11px]">Browse Events</Link>
        </div>
       ) : (
        <div className="space-y-3">
          {rows.map((p) => (
            <div key={p.id} data-testid={`purchase-row-${p.id}`} className="glass chrome-border rounded-md p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="w-11 h-11 rounded-full chrome-border bg-black/40 flex items-center justify-center shrink-0">
                <ShoppingBag className="w-4 h-4 text-[#ff2e93]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] tracking-luxe uppercase text-white/45">Purchase</span>
                  <span className="text-[10px] text-white/55 font-mono">{p.id.slice(0, 8)}</span>
                  {p.download_locked ? (
                    <span className="inline-flex items-center gap-1 text-[9px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border border-white/15 text-white/55">
                      <Lock className="w-3 h-3" /> Download locked
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[9px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border border-[#7fe6ff]/40 text-[#7fe6ff]">
                      Downloads available
                    </span>
                  )}
                </div>
                <p className="font-display text-lg text-chrome mt-0.5">{p.total_photos} photo{p.total_photos === 1 ? "" : "s"} · {p.total_credits_used} credits</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">
                  <Calendar className="w-3 h-3 inline mr-1" />{fmtDate(p.created_at)}
                  {p.pricing_breakdown?.length > 0 && <> · {p.pricing_breakdown.length} event{p.pricing_breakdown.length === 1 ? "" : "s"}</>}
                </p>
                {!p.download_locked && (
                  <div data-testid={`purchase-download-progress-${p.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border border-[#7fe6ff]/30 text-[#7fe6ff]">
                    {p.photos_downloaded ?? 0} of {p.total_photos} downloaded
                  </div>
                )}
                {p.pricing_breakdown?.length > 0 && (
                  <ul className="text-xs text-white/55 mt-2 space-y-0.5">
                    {p.pricing_breakdown.slice(0, 3).map((b, i) => (
                      <li key={i} className="truncate">{b.event_title} — {b.selected_count} photo{b.selected_count === 1 ? "" : "s"} · {b.total_credits} cr</li>
                    ))}
                  </ul>
                )}
              </div>
              <Link to="/customer/purchased" data-testid={`purchase-view-${p.id}`} className="btn-ghost !py-2 !px-4 !text-[10px] shrink-0">
                View Photos <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
       )
      }
    </div>
  );
}
